import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import type { OverridableComponent } from '@mui/material/OverridableComponent';
import Stack from '@mui/material/Stack';
import type { SvgIconTypeMap } from '@mui/material/SvgIcon';
import { type ChangeEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DebounceField } from '../../../DebounceField/DebounceField';
import { palette } from '../../../theme/paletteV2';

export type ListTopBarOption = {
  Icon: OverridableComponent<SvgIconTypeMap>;
  hasBottomDivider?: boolean;
  name: string;
  onClick: (anchor: HTMLButtonElement | null) => void;
};

export type ListTopBarProps = {
  isDisabled?: boolean;
  isLoading?: boolean;
  options?: ListTopBarOption[];
  searchBarPlaceholder?: string;
  searchInputDataTest?: string;
  searchPhrase: string;
  setSearchPhrase: (phrase: string) => void;
};

export const ListTopBar = (props: ListTopBarProps) => {
  const { t } = useTranslation();

  const {
    isDisabled = false,
    isLoading = false,
    options,
    searchBarPlaceholder = t('Search Name'),
    searchInputDataTest,
    searchPhrase,
    setSearchPhrase,
  } = props;

  const hasOptions = !!options?.length;

  const menuRef = useRef<HTMLButtonElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        backgroundColor: isDisabled ? palette.action.disabled : 'default',
      }}
    >
      <DebounceField
        debounceTimeout={500}
        disabled={isDisabled}
        fullWidth
        slotProps={{
          htmlInput: {
            'data-test': searchInputDataTest,
          },
          input: {
            endAdornment: (
              <>
                {isLoading && (
                  <Box>
                    <CircularProgress size={20} />
                  </Box>
                )}

                {searchPhrase && !isLoading && (
                  <IconButton
                    aria-label="Clear search box"
                    data-test="close-search"
                    disabled={isDisabled}
                    edge="end"
                    onClick={() => setSearchPhrase('')}
                    size="small"
                  >
                    <CloseIcon />
                  </IconButton>
                )}
              </>
            ),
            sx: {
              '&.MuiInputBase-root': {
                borderWidth: 0,
              },
              height: 4,
            },
          },
        }}
        onChange={(event: ChangeEvent<HTMLInputElement>) => setSearchPhrase(event.target.value)}
        placeholder={searchBarPlaceholder}
        type="search"
        value={searchPhrase}
        variant="outlined"
        data-test="debounce-field-input"
      />

      {hasOptions && (
        <IconButton
          aria-label="Options menu"
          data-test="search-bar-menu-buton"
          disabled={isDisabled}
          onClick={() => setIsMenuOpen(prevState => !prevState)}
          ref={menuRef}
        >
          <MoreVertIcon />
        </IconButton>
      )}

      <Menu
        anchorEl={menuRef.current}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        onClose={() => setIsMenuOpen(false)}
        open={isMenuOpen}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
      >
        {options?.map(({ hasBottomDivider, Icon, name, onClick }, index) => (
          <MenuItem
            dense
            divider={hasBottomDivider}
            key={`${index}-${name}`}
            onClick={() => {
              setIsMenuOpen(false);
              onClick?.(menuRef.current);
            }}
          >
            <ListItemIcon>
              <Icon />
            </ListItemIcon>
            <ListItemText data-test="filter-name">{name}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
};
